import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionSize,
} from '@havenengineering/module-shared-library/dist/components/Accordion/Accordion';
import InlineMessaging, {
  InlineMessagingTheme,
} from '@havenengineering/module-shared-library/dist/components/InlineMessaging/InlineMessaging';
import { SelectInputField } from '@havenengineering/module-shared-library/dist/form/components/SelectInput';
import { TextInputField } from '@havenengineering/module-shared-library/dist/form/components/TextInput';
import useForm, {
  ukPhoneValidator,
} from '@havenengineering/module-shared-library/dist/form/hooks/useForm';
import { Button } from '@havenengineering/module-shared-owners-ui/dist/components/Button';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';

import { useGuestList } from '../../../hooks/useGuestList';
import { BookingType } from '../../../types/bookingType';
import ParticipantForm from '../../BookingDetails/ParticipantForm';
import { CounterButtons } from '../../CounterButtons/CounterButtons';
import { getGuestSelectorOptions } from '../../helpers/bookings';
import { ArrivalBookingForm } from '../Booking';
import styles from './PersonDetailsStep.module.scss';
import { Stepper } from './Stepper';

type PersonDetailsStepProps = {
  bookingType: BookingType;
  handleNext: (data: any) => void;
  handlePrev: () => void;
  form: Partial<ArrivalBookingForm>;
  nextIsDisabled: boolean;
  bookingError: string;
  fandfCards: OwnersCard[] | null;
  handleSetInfo: (message: string) => void;
};

export const PersonDetailsStep: FunctionComponent<PersonDetailsStepProps> = ({
  bookingType,
  handleNext,
  handlePrev,
  form,
  nextIsDisabled,
  bookingError,
  fandfCards,
  handleSetInfo,
}) => {
  const { activeAccount } = useAuthContext();
  const [childCount, setChildCount] = useState(form.childNumber || 0);
  const [infantsCount, setInfantsCount] = useState(form.toddlersNumber || 0);
  const { rows, guests, addGuest, updateGuest, removeGuest } = useGuestList(
    form.participants || []
  );
  const [editingRowKey, setEditingRowKey] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);

  const guestSelectorOptions = useMemo(
    () => getGuestSelectorOptions({ fandfCards, activeAccount }),
    [activeAccount, fandfCards]
  );

  const { fields, submit } = useForm<any>({
    initialState: {
      firstName: form.firstName || '',
      lastName: form.lastName || '',
      letterEmail: form.letterEmail || '',
      phoneNumber: form.phoneNumber || '',
      childNumber: form.childNumber || 0,
      toddlersNumber: form.toddlersNumber || 0,
      privCardHolder: form.privCardHolder || '',
    },
    schema: {
      firstName: {
        required: true,
      },
      lastName: {
        required: true,
      },
      letterEmail: {
        required: bookingType === BookingType.PRIVATE_LET,
      },
      phoneNumber: {
        required: true,
        validator: ukPhoneValidator,
      },
    },
    submitCallback: async (state) => {
      handleNext({
        ...state,
        childNumber: childCount,
        toddlersNumber: infantsCount,
        participants: guests,
      });
    },
  });

  useEffect(() => {
    const details = fandfCards?.find(
      (card) => card.id == fields.privCardHolder.value
    );
    if (details) {
      fields.firstName.setValue(details?.firstName || '');
      fields.lastName.setValue(details?.lastName || '');
      fields.phoneNumber.setValue(details?.phoneNumber || '');
      fields.letterEmail.setValue(details?.email || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields.privCardHolder.value]);

  useEffect(() => {
    return () => {
      handleSetInfo('');
    };
  }, [handleSetInfo]);

  const handleParticipantSave = (participant: Participant) => {
    addGuest(participant);
    setShowForm(false);
  };

  const handleParticipantEdit = (rowKey: string, participant: Participant) => {
    updateGuest(rowKey, participant);
    setEditingRowKey(null);
  };

  const handleCancelEdit = () => {
    setEditingRowKey(null);
    setShowForm(false);
  };

  const rowBeingEdited = rows.find(({ rowKey }) => rowKey === editingRowKey);

  return (
    <div className={styles.mainContainer}>
      {bookingType === BookingType.OWNER && (
        <h1 className={styles.label}>Your details</h1>
      )}
      {bookingType === BookingType.PRIVATE_LET && (
        <h1 className={styles.label}>Your Guest&apos;s details</h1>
      )}
      <p className={styles.text}>
        Please provide contact details of the lead guest. We will only contact
        them should we need to before or during their stay.
      </p>
      {bookingType === BookingType.PRIVATE_LET && (
        <InlineMessaging
          type="info"
          title="Info"
          message="All guests will be offered the option to purchase a Play Pass in their welcome email"
          dismissible={false}
        />
      )}
      <form
        className={styles.editorContainer}
        onSubmit={(event) => event.preventDefault()}
      >
        {bookingType !== BookingType.PRIVATE_LET && (
          <>
            <TextInputField
              className="fs-mask"
              id="firstName"
              label="First name"
              field={fields.firstName}
              type="text"
            />
            <TextInputField
              className="fs-mask"
              id="lastName"
              label="Surname"
              field={fields.lastName}
              type="text"
            />
            <TextInputField
              className="fs-mask"
              id="phoneNumber"
              label="Contact number"
              field={fields.phoneNumber}
              type="text"
            />
          </>
        )}
        {bookingType === BookingType.PRIVATE_LET && (
          <>
            {guestSelectorOptions.length > 0 && (
              <SelectInputField
                className="fs-mask"
                id="privCardHolder"
                label="Guest Options"
                field={fields.privCardHolder}
                options={guestSelectorOptions as any}
              />
            )}
            <TextInputField
              className="fs-mask"
              id="firstName"
              label="Lead guest first name"
              field={fields.firstName}
              type="text"
            />
            <TextInputField
              className="fs-mask"
              id="lastName"
              label="Lead guest surname"
              field={fields.lastName}
              type="text"
            />
            <TextInputField
              className="fs-mask"
              id="phoneNumber"
              label="Contact number"
              field={fields.phoneNumber}
              type="text"
            />
            <TextInputField
              className="fs-mask"
              id="letterEmail"
              label="Email address"
              field={fields.letterEmail}
              type="text"
            />
          </>
        )}
      </form>

      {bookingType === BookingType.PRIVATE_LET && (
        <>
          <h1 className={styles.label}>Additional guests</h1>
          <div className={styles.row}>
            <p>
              <strong>Adults</strong>
              <br />
              <small>16+ years</small>
            </p>
            <Button
              className={styles.iconButton}
              variant="outlined"
              onClick={() => setShowForm(true)}
              type="button"
              disabled={!!rowBeingEdited}
              data-testid="add-adult-btn"
            >
              <img
                src="https://www.haven.com/assets/icons/plus.svg"
                width={24}
                height={24}
                alt="add"
              />
            </Button>
          </div>
          {rows.map(({ rowKey, guest }, idx) => (
            <div key={rowKey} className={styles.row}>
              <p>
                <strong>{guest.name}</strong>
                <br />
                <small>{guest.phoneNumber}</small>
              </p>
              <div>
                <Button
                  className={styles.iconButton}
                  variant="outlined"
                  disabled={showForm}
                  onClick={() => removeGuest(rowKey)}
                  type="button"
                  data-testid={`delete-${idx}`}
                >
                  <img
                    src="/assets/icon-delete.svg"
                    width={24}
                    height={24}
                    alt="add"
                  />
                </Button>
                <Button
                  className={styles.iconButton}
                  variant="outlined"
                  disabled={showForm}
                  onClick={() => setEditingRowKey(rowKey)}
                  type="button"
                  data-testid={`edit-${idx}`}
                >
                  <img
                    src="https://www.haven.com/assets/icons/pencil-blue.svg"
                    width={24}
                    height={24}
                    alt="add"
                  />
                </Button>
              </div>
            </div>
          ))}
          {rowBeingEdited && (
            <ParticipantForm
              participant={rowBeingEdited.guest}
              handleSaveParticipant={(participant) =>
                handleParticipantEdit(rowBeingEdited.rowKey, participant)
              }
              handleCancel={() => handleCancelEdit()}
            />
          )}
          {showForm && (
            <ParticipantForm
              handleSaveParticipant={handleParticipantSave}
              handleCancel={() => handleCancelEdit()}
            />
          )}

          <div className={styles.row}>
            <p>
              Children
              <br />
              <small>5–15 years</small>
            </p>
            <div>
              <CounterButtons
                count={childCount}
                onDecrement={(count) => setChildCount(count)}
                onIncrement={(count) => setChildCount(count)}
                testId="children"
              />
            </div>
          </div>
          <div className={styles.row}>
            <p>
              Infants
              <br />
              <small>0–4 years</small>
            </p>
            <div>
              <CounterButtons
                count={infantsCount}
                onDecrement={(count) => setInfantsCount(count)}
                onIncrement={(count) => setInfantsCount(count)}
                testId="infants"
              />
            </div>
          </div>
        </>
      )}
      <Accordion
        title="Read how we process this data"
        size={AccordionSize.SMALL}
      >
        <p>
          This information has been intentionally minimised to ensure we are
          only collecting the data that is necessary to enable us to ensure
          efficiencies on arrival, maintain social distancing measures and
          communicate with you in case of an emergency. We will retain this data
          for a maximum of 30 days after you or your guest has departed. At the
          end of that period your data will either be deleted completely or
          anonymised. Data Protection principles will, of course, apply as usual
          and your data will not be further processed.
        </p>
      </Accordion>

      {bookingError && (
        <InlineMessaging
          type={InlineMessagingTheme.ERROR_THEME}
          title="Error"
          message={bookingError}
          dismissible={false}
        />
      )}

      <Stepper
        handleNext={() => submit()}
        handlePrev={() => handlePrev()}
        nextIsDisabled={nextIsDisabled}
        prevIsDisabled={false}
        dotsCount={3}
        activeDot={2}
        nextLabel="Book"
      />
    </div>
  );
};
